import React, { useEffect, useRef, useState } from "react";
import groupPhoto from "../assets/vels-photo.jpg";
import { motion, useInView } from "framer-motion";

function CountUpStat({ value, suffix = "+", duration = 1800 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    let animationFrame;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(eased * value);
      setCount(current);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, value, duration]);

  return (
    <div
      ref={ref}
      className="text-[42px] sm:text-[56px] md:text-[62px] lg:text-[72px] xl:text-[96px] font-bold leading-none tracking-[-0.02em] text-white"
    >
      {count}
      {suffix}
    </div>
  );
}

export default function StatsGridSection() {

  const stats = [
    {
      value: 10,
      suffix: "+",
      small:
        "Preferred Academic Partner For leading\nIndian universities and higher education\ninstitutions",
    },
    {
      value: 3000,
      suffix: "+",
      small:
        "Students trained through partner Training\nDelivery Centers across India and\noverseas",
    },
    {
      value: 10,
      suffix: "+",
      small:
        "Training Delivery Centers across India and\ninternational locations",
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const zoomWrap = {
    hidden: { opacity: 0, scale: 0.98 },
    show: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.75, ease: "easeOut" },
    },
  };

  const zoomImg = {
    hidden: { scale: 1.08 },
    show: {
      scale: 1,
      transition: { duration: 1.25, ease: "easeOut" },
    },
  };

  return (
    <section className="bg-white px-4 sm:px-6 lg:px-8 xl:px-10 py-8 sm:py-12 lg:py-14">
      <div className="max-w-[1700px] mx-auto overflow-hidden rounded-[20px] sm:rounded-[28px] bg-black shadow-[0_30px_90px_rgba(0,0,0,0.22)]">
        {/* TOP STATS BAR */}
        <div className="xl:grid xl:grid-cols-12 border-b border-white/10">
          {/* LEFT EMPTY BLACK SPACE */}
          <div className="hidden xl:block xl:col-span-3 relative min-h-[250px]">
            <div className="absolute top-0 bottom-0 right-0 w-px bg-white/20" />
          </div>

          {/* STATS */}
          <div className="xl:col-span-9">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 md:gap-10 xl:gap-12 px-5 sm:px-7 md:px-8 lg:px-10 py-8 sm:py-10 lg:py-12 xl:py-14">
              {stats.map((s, i) => (
                <motion.div
                  key={s.value + String(i)}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.35 }}
                  className={`
                    ${i === 2 ? "md:col-span-2 xl:col-span-1" : ""}
                  `}
                >
                  <CountUpStat value={s.value} suffix={s.suffix} />
                  <p className="mt-4 sm:mt-5 lg:mt-6 max-w-[360px] text-[13px] sm:text-[14px] md:text-[15px] lg:text-[17px] xl:text-[18px] leading-[1.35] text-white/90 font-semibold whitespace-pre-line">
                    {s.small}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* IMAGE ROW */}
        <div className="bg-black px-4 sm:px-5 md:px-6 lg:px-7 xl:px-8 pb-4 sm:pb-5 lg:pb-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5 lg:gap-6 xl:gap-8 items-stretch">
            {/* LEFT BIG GROUP IMAGE */}
            <motion.div
              className="lg:col-span-8 xl:col-span-9"
              variants={zoomWrap}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
            >
              <div className="overflow-hidden rounded-[14px] sm:rounded-[18px] h-[240px] sm:h-[320px] md:h-[380px] lg:h-[430px] xl:h-[500px]">
                <motion.img
                  variants={zoomImg}
                  src="https://mhcglobal.info/Media/Home/graduation.webp"
                  alt="MH Cockpit aviation students celebrating graduation at Vels University"
                  title="Aviation Graduates – MH Cockpit Partner College Vels University Chennai"
                  className="w-full h-full object-cover"
                  draggable="false"
                  loading="lazy"
                />
              </div>
            </motion.div>

            {/* RIGHT PLANE IMAGE */}
            <motion.div
              className="lg:col-span-4 xl:col-span-3"
              variants={zoomWrap}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
            >
              <div className="relative overflow-hidden rounded-[14px] sm:rounded-[18px] xl:rounded-none h-[220px] sm:h-[280px] md:h-[320px] lg:h-[430px] xl:h-[500px] bg-black">
                <motion.img
                  variants={zoomImg}
                  src="https://mhcglobal.info/Media/Home/cessna-n63392.webp"
                  alt="Cessna aircraft N63392 in flight over countryside"
                  title="Cessna Training Aircraft – MH Cockpit Aviation Academy"
                  className="w-full h-full object-cover"
                  draggable="false"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                <div className="absolute left-4 sm:left-5 md:left-6 bottom-4 sm:bottom-5 md:bottom-6 z-10">
                  <p className="text-[10px] sm:text-[11px] md:text-[12px] tracking-[0.12em] uppercase text-white leading-[1.4]">
                    For globally recognised flight
                    <br />
                    training organisations (FTOs)
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* BOTTOM CARDS ROW */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-0 border-t border-white/10">
          {/* LEFT LOGO CARD */}
          <motion.div
            className="relative bg-black p-4 sm:p-5 lg:p-6 xl:p-7 border-b md:border-r md:border-b border-white/10 xl:border-b-0"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <div className="rounded-[14px] bg-white p-4 sm:p-5 lg:p-6 h-[220px] sm:h-[240px] md:h-[250px] lg:h-[300px] xl:h-[350px] flex items-center justify-center">
              <img
                src="https://mhcglobal.info/Media/Home/dpiit-startupindia-logo.webp"
                alt="DPIIT Startup India recognised institution logo"
                title="DPIIT Startup India Recognition – MH Cockpit"
                className="max-h-[68px] sm:max-h-[76px] md:max-h-[82px] lg:max-h-[90px] max-w-full object-contain"
                draggable="false"
                loading="lazy"
              />
            </div>

            <div className="mt-4">
              <span className="inline-flex items-center rounded-full border border-white px-3 py-1 text-[10px] sm:text-[12px] lg:text-[14px] xl:text-[17px] uppercase tracking-wide text-white">
                We are accredited by
              </span>
            </div>
          </motion.div>

          {/* CENTER IMAGE CARD */}
          <motion.div
            className="relative bg-black p-4 sm:p-5 lg:p-6 xl:p-7 border-b xl:border-r border-white/10"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <div className="rounded-[14px] overflow-hidden h-[220px] sm:h-[240px] md:h-[250px] lg:h-[300px] xl:h-[350px]">
              <motion.img
                variants={zoomImg}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.25 }}
                src="https://mhcglobal.info/Media/Home/become-a-pilot-aviation-courses-trainter-at-cockpit.webp"
                alt="Young female pilot in uniform giving thumbs up"
                title="Become a Pilot – Aviation Courses at MH Cockpit"
                className="w-full h-full object-cover"
                draggable="false"
                loading="lazy"
              />
            </div>

            <div className="mt-4">
              <span className="inline-flex items-center rounded-full border border-white px-3 py-1 text-[10px] sm:text-[12px] lg:text-[14px] xl:text-[17px] uppercase tracking-wide text-white">
                Aviation
              </span>
              <p className="mt-2 text-[12px] sm:text-[13px] md:text-[14px] lg:text-[15px] xl:text-[17px] text-white leading-[1.5] max-w-[350px]">
                Build your future in a structured university-led aviation
                ecosystem
              </p>
            </div>
          </motion.div>

          {/* RIGHT LOGO CARD */}
          <motion.div
            className="relative bg-black p-4 sm:p-5 lg:p-6 xl:p-7 md:col-span-2 xl:col-span-1"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <div className="rounded-[14px] bg-white p-4 sm:p-5 lg:p-6 h-[220px] sm:h-[240px] md:h-[250px] lg:h-[300px] xl:h-[350px] flex items-center justify-center">
              <img
                src="https://mhcglobal.info/Media/Home/msme-logo.webp"
                alt="MSME Micro Small and Medium Enterprises certified organisation logo"
                title="MSME Certified – MH Cockpit Aviation Academy"
                className="max-h-[90px] sm:max-h-[100px] md:max-h-[110px] lg:max-h-[130px] xl:max-h-[160px] max-w-full object-contain"
                draggable="false"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>

        <div className="h-[14px] sm:h-[18px] lg:h-[22px] bg-black" />
      </div>
    </section>
  );
}